import { useState, useEffect } from 'react'
import { X, Star, Zap, Wrench } from 'lucide-react'
import { Button } from '@/components/atoms/Button'
import { Modal } from '@/components/molecules/Modal'
import { authedFetch } from '@/lib/api'

interface ChangelogEntry {
  id: number
  version: string
  title: string
  summary: string
  category: 'New Feature' | 'Improvement' | 'Bug Fix'
  is_major: boolean
  published_at: string
}

const CATEGORY_ICON: Record<string, React.ReactNode> = {
  'New Feature': <Star size={14} className="text-yellow-400" />,
  'Improvement': <Zap size={14} className="text-blue-400" />,
  'Bug Fix': <Wrench size={14} className="text-green-400" />,
}

const STORAGE_KEY = 'tenon_whats_new_dismissed'

export function WhatsNewModal() {
  const [entries, setEntries] = useState<ChangelogEntry[]>([])
  const [open, setOpen] = useState(false)

  useEffect(() => {
    // Only show once per session
    if (sessionStorage.getItem(STORAGE_KEY)) return
    authedFetch('/api/v1/changelog/unread', {}, { redirectOnAuthFailure: false })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d && d.count > 0) {
          setEntries(d.entries)
          setOpen(true)
        }
      })
      .catch(() => {})
  }, [])

  const handleClose = () => {
    setOpen(false)
    sessionStorage.setItem(STORAGE_KEY, '1')
    // Mark all as read
    authedFetch('/api/v1/changelog/read-all', {
      method: 'POST',
    }, { redirectOnAuthFailure: false }).catch(() => {})
  }

  if (!open || entries.length === 0) return null

  return (
    <Modal open={open} onClose={handleClose} title="What's New">
      <div className="space-y-4 max-h-96 overflow-y-auto pr-1">
        {entries.map(e => (
          <div key={e.id} className="border border-neutral-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              {CATEGORY_ICON[e.category] ?? null}
              <span className="text-xs text-neutral-400">{e.category}</span>
              {e.is_major && (
                <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(239,248,144,0.15)', color: 'var(--brand-gold)' }}>
                  Major
                </span>
              )}
              <span className="text-xs text-neutral-500 ml-auto">{e.version}</span>
            </div>
            <h4 className="font-semibold text-white text-sm mb-1">{e.title}</h4>
            <p className="text-sm text-neutral-300">{e.summary}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-700">
        <a href="/changelog" className="text-sm" style={{ color: 'var(--brand-gold)' }}>
          See all updates →
        </a>
        <Button variant="primary" size="sm" onClick={handleClose}>
          Got it
        </Button>
      </div>
    </Modal>
  )
}
